import Immutable from 'immutable';
import { getPreference, setPreference } from '../../data/preferences';
import { resultSetFromObjectArray } from '../../data/resultsetutils';
import { createModelForState } from '../moduleregistry';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

let columnWidthStore = {};


function getColumnWidthPreferenceKey(entityName, profile) {
	return 'resultSet//' + (entityName || '__default__') + '//' + (profile || '__default__') + '//columnWidths';
}

function setColumnWidthPreference(entityName, profile, columnWidthMap) {
	setPreference(getColumnWidthPreferenceKey(entityName, profile), columnWidthMap);
}

function getColumnWidthPreference(entityName, profile) {
	return getPreference(getColumnWidthPreferenceKey(entityName, profile)) || EMPTY_MAP;
}


export default class ResultSetModel {
	#data;

	constructor(data = null) {
		this.#data = data || EMPTY_MAP;
	}

	setProfile(value) {
		return new ResultSetModel(this.#data.set('profile', value));
	}

	setRowChecked(rowIndex, value) {
		return new ResultSetModel(this.#data.setIn(['rows', rowIndex, 'checked'], value));
	}

	setAllRowsChecked(value) {
		const rows = (this.#data.get('rows') || EMPTY_LIST);
		return new ResultSetModel(this.#data.set('rows', rows.map(row => row.set('checked', value))));
	}

	toggleRowChecked(rowIndex) {
		const data = this.#data;
		if (!data.get('withRowCheckBox')) {
			return this;
		}
		return this.setRowChecked(rowIndex, !data.getIn(['rows', rowIndex, 'checked']));
	}

	getCheckedRows() {
		return (this.#data.get('rows') || EMPTY_LIST).filter(row => row.get('checked'));
	}

	setWithRowCheckBox(value) {
		return new ResultSetModel(this.#data.set('withRowCheckBox', value));
	}

	setEntityName(entityName) {
		return new ResultSetModel(this.#data.set('entityName', entityName));
	}

	setSourceUri(uri) {
		return new ResultSetModel(this.#data.set('sourceUri', uri));
	}

	getEntityName() {
		return this.#data.get('entityName');
	}

	setGetErrorMessage(func) {
		return new ResultSetModel(this.#data.set('getErrorMessage', func));
	}

	getData() {
		return this.#data;
	}

	getColumnWidths() {
		return this.#data.get('columnWidths') || EMPTY_LIST;
	}

	getColumnName(columnIndex) {
		return this.#data.getIn(['columns', columnIndex, 'name']);
	}

	getProfile() {
		return this.#data.get('profile') || '__default__';
	}

	setColumnWidth(columnIndex, columnWidth) {
		const data = this.#data;
		const entityName = data.get('entityName');
		const columnWidths = this.getColumnWidths().set(columnIndex, columnWidth);
		const columnName = data.getIn(['columns', columnIndex, 'name']);
		const profile = this.getProfile();
		setColumnWidthPreference(entityName, profile, getColumnWidthPreference(entityName, profile).set(columnName, columnWidth));
		return new ResultSetModel(data.set('columnWidths', columnWidths));
	}

	setColumnWidths(columnWidths) {
		const data = this.#data;
		const entityName = data.get('entityName');
		const profile = this.getProfile();
		let colWidthMap = getColumnWidthPreference(entityName, profile);
		columnWidths = Immutable.fromJS(columnWidths).map(cw => cw > 600 ? 600 : cw);
		(data.get('columns') || EMPTY_LIST).forEach((col, index) => {
			colWidthMap = colWidthMap.set(col.get('name'), columnWidths.get(index));
		})
		setColumnWidthPreference(entityName, profile, colWidthMap);
		const columns = this.#data.get('columns') || EMPTY_LIST;
		return new ResultSetModel(data.set('columnWidths', columnWidths));
	}

	setPreferredColumnWidths() {
		let data = this.#data;
		const entityName = data.get('entityName');
		const colWidthMap = getColumnWidthPreference(entityName, this.getProfile());
		const columnWidths = (data.get('columns') || EMPTY_LIST).map(col => colWidthMap.get(col.get('name')));
		data = data.set('columnWidths', columnWidths);
		if (columnWidths.some(cw => typeof cw !== 'number')) {
			data = data.set('calculateColumnWidthTimestamp', new Date().getTime());
		}
		return new ResultSetModel(data);
	}

	getSelectedRowIndex() {
		return this.#data.get('selectedRowIndex') || 0;
	}

	setFixedColumns(value) {
		return new ResultSetModel(this.#data.set('fixedColumns', value));
	}

	getSelectedRow() {
		const rows = this.#data.get('rows');
		if (!rows) {
			return null;
		}
		return rows.get(this.getSelectedRowIndex());
	}

	getRow(rowIndex) {
		const rows = this.#data.get('rows');
		if (!rows) {
			return null;
		}
		return rows.get(rowIndex);
	}

	setRow(rowIndex, row) {
		let rows = this.#data.get('rows');
		if (!rows || rows.size <= rowIndex) {
			return this;
		}
		return new ResultSetModel(this.#data.set('rows', rows.set(rowIndex, row)));
	}

	getRowIndex(pars) {
		let parKeys = [];
		for (let key in pars) {
			parKeys.push(key);
		}
		if (parKeys.length == 1) {
			const key = parKeys[0];
			const value = pars[key];
			return (this.getRows() || EMPTY_LIST).findIndex(r => r.get(key) === value);
		} else {
			// todo: vice filtru
			return -1;
		}
	}

	getRows() {
		return this.#data.get('rows');
	}

	setSelectedRowIndex(rowIndex) {
		const rows = this.#data.get('rows');
		if (!rows) {
			return this;
		}
		if (rowIndex < 0) {
			rowIndex = 0;
		} else if (rowIndex >= rows.size) {
			rowIndex = rows.size - 1;
		}
		return new ResultSetModel(this.#data.set('selectedRowIndex', rowIndex));
	}

	setFilterEditorFilter(value) {
		return new ResultSetModel(this.#data.setIn(['filterEditor', 'filter'], value || EMPTY_MAP));
	}

	showFilterEditor() {
		const data = this.#data;
		return new ResultSetModel(data.set('filterEditor', Immutable.Map({
			entityName: data.get('entityName'),
			sourceUri: data.get('sourceUri'),
			filterColumns: data.get('filterColumns'),
			filter: data.get('filter')
		})));

	}

	setFilter(value) {
		return new ResultSetModel(this.#data.set('filter', value));
	}

	showColumnListEditor() {
		const data = this.#data;
		return new ResultSetModel(data.set('columnListEditor', Immutable.Map({
			entityName: data.get('entityName'),
			availableColumns: data.get('availableColumns'),
			selectedColumns: data.get('columns').map(col => col.get('name'))
		})));
	}

	moveColumnBefore(col, before) {
		let cle = this.#data.get('columnListEditor');
		if (!cle) {
			return this;
		}
		let sc = cle.get('selectedColumns').filter(c => c !== col);
		let idx = -1;
		if (before) {
			idx = sc.findIndex(c => c === before);
		}
		if (idx < 0) {
			sc = sc.push(col);
		} else {
			sc = sc.slice(0, idx).push(col).concat(sc.slice(idx));
		}
		cle = cle.set('selectedColumns', sc);
		return new ResultSetModel(this.#data.set('columnListEditor', cle));
	}

	removeAllColumns() {
		let cle = this.#data.get('columnListEditor');
		if (!cle) {
			return this;
		}
		cle = cle.set('selectedColumns', EMPTY_LIST);
		return new ResultSetModel(this.#data.set('columnListEditor', cle));
	}

	removeColumn(col) {
		let cle = this.#data.get('columnListEditor');
		if (!cle) {
			return this;
		}
		cle = cle.set('selectedColumns', cle.get('selectedColumns').filter(c => c !== col));
		return new ResultSetModel(this.#data.set('columnListEditor', cle));
	}

	hideFilterEditor() {
		return new ResultSetModel(this.#data.delete('filterEditor'));
	}

	hideColumnListEditor() {
		return new ResultSetModel(this.#data.delete('columnListEditor'));
	}

	setResultSet(rs) {
		let immRs = Immutable.fromJS(rs || {});
		return new ResultSetModel(
			this.#data.set('availableColumns', immRs.get('availableColumns'))
				.set('columns', immRs.get('columns'))
				.set('rows', immRs.get('rows'))
				.set('filterColumns', immRs.get('filterColumns'))
				.set('aggregates', immRs.get('aggregates'))
				.set('pager', immRs.get('pager'))
			).setPreferredColumnWidths();
	}

	setFromObjectArray(arr, options) {
		return this.setResultSet(resultSetFromObjectArray(arr, options));
	}

	getColumnTextMap() {
		let res = {};
		const cols = this.#data.get('columns');
		if (cols) {
			cols.forEach(col => {
				res[col.get('name')] = col.get('text');
			})
		}
		return res;
	}

	getState() {
		return Immutable.Map({
			__module: 'ResultSet',
			data: this.#data
		});
	}
}
